import { useState } from 'react';
import { Search, HelpCircle, Book, Video, MessageCircle, ChevronDown, ChevronRight } from 'lucide-react';

const HelpPage = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedCategory, setExpandedCategory] = useState<string | null>(null);

  const helpCategories = [
    {
      id: 'getting-started',
      title: 'Getting Started',
      icon: Book,
      articles: [
        { title: 'How to create an account', content: 'Step-by-step guide to setting up your account.' },
        { title: 'Choosing the right course', content: 'Tips for selecting courses that match your skill level.' },
        { title: 'Understanding course bundles', content: 'Learn about our course bundles and their benefits.' },
        { title: 'System requirements', content: 'Technical requirements for our courses.' }
      ]
    },
    {
      id: 'courses',
      title: 'Courses & Learning',
      icon: Video,
      articles: [
        { title: 'How to enroll in courses', content: 'Complete guide to course enrollment process.' },
        { title: 'Accessing course materials', content: 'How to access videos, resources, and assignments.' },
        { title: 'Progress tracking', content: 'Monitor your learning progress and completion status.' },
        { title: 'Certificates of completion', content: 'How to earn and download your certificates.' }
      ]
    },
    {
      id: 'payment',
      title: 'Payment & Billing',
      icon: MessageCircle,
      articles: [
        { title: 'Payment methods accepted', content: 'List of supported payment options.' },
        { title: 'Refund policy', content: 'Understanding our 30-day money-back guarantee.' },
        { title: 'Bundle pricing', content: 'How bundle pricing works and savings information.' },
        { title: 'Invoice and receipts', content: 'How to access your payment receipts.' }
      ]
    },
    {
      id: 'technical',
      title: 'Technical Support',
      icon: HelpCircle,
      articles: [
        { title: 'Video playback issues', content: 'Troubleshooting video streaming problems.' },
        { title: 'Download problems', content: 'Solutions for resource download issues.' },
        { title: 'Browser compatibility', content: 'Supported browsers and settings.' },
        { title: 'Mobile app support', content: 'Learning on mobile devices and tablets.' }
      ]
    }
  ];

  const filteredCategories = helpCategories.map(category => ({
    ...category,
    articles: category.articles.filter(article =>
      article.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      article.content.toLowerCase().includes(searchTerm.toLowerCase())
    )
  })).filter(category => category.articles.length > 0 || searchTerm === '');

  const toggleCategory = (categoryId: string) => {
    setExpandedCategory(expandedCategory === categoryId ? null : categoryId);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-br from-green-500 via-emerald-600 to-teal-600 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              <span className="bg-gradient-to-r from-green-100 to-emerald-100 bg-clip-text text-transparent">Help</span> Center
            </h1>
            <p className="text-xl text-green-50 max-w-3xl mx-auto mb-8">
              Find answers to your questions and get the support you need to succeed in your AI testing journey.
            </p>
            
            {/* Search */}
            <div className="max-w-2xl mx-auto">
              <div className="relative">
                <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
                <input
                  type="text"
                  placeholder="Search for help articles..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-12 pr-4 py-4 text-gray-900 bg-white border border-green-200 rounded-xl focus:ring-2 focus:ring-green-300 focus:border-transparent shadow-lg"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Help Categories */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Categories List */}
          <div className="lg:col-span-2">
            <div className="space-y-6">
              {filteredCategories.map((category) => {
                const Icon = category.icon;
                const isExpanded = expandedCategory === category.id;
                
                return (
                  <div key={category.id} className="bg-white rounded-2xl shadow-lg border border-gray-200">
                    <button
                      onClick={() => toggleCategory(category.id)}
                      className="w-full p-6 text-left flex items-center justify-between hover:bg-gray-50 transition-colors rounded-2xl"
                    >
                      <div className="flex items-center space-x-4">
                        <div className="p-3 bg-green-100 rounded-xl">
                          <Icon className="h-6 w-6 text-green-600" />
                        </div>
                        <div>
                          <h3 className="text-xl font-bold text-gray-900">{category.title}</h3>
                          <p className="text-gray-600">{category.articles.length} articles</p>
                        </div>
                      </div>
                      {isExpanded ? (
                        <ChevronDown className="h-5 w-5 text-gray-400" />
                      ) : (
                        <ChevronRight className="h-5 w-5 text-gray-400" />
                      )}
                    </button>
                    
                    {isExpanded && (
                      <div className="px-6 pb-6 space-y-4 border-t border-gray-100">
                        {category.articles.map((article, index) => (
                          <div key={index} className="p-4 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors cursor-pointer">
                            <h4 className="font-semibold text-gray-900 mb-2">{article.title}</h4>
                            <p className="text-gray-600 text-sm">{article.content}</p>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Quick Actions */}
            <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-200">
              <h3 className="text-lg font-bold text-gray-900 mb-4">Quick Actions</h3>
              <div className="space-y-3">
                <a href="/contact" className="block p-3 bg-green-50 hover:bg-green-100 rounded-xl transition-colors">
                  <div className="font-semibold text-green-800">Contact Support</div>
                  <div className="text-sm text-green-600">Get personalized help</div>
                </a>
                <a href="/courses" className="block p-3 bg-blue-50 hover:bg-blue-100 rounded-xl transition-colors">
                  <div className="font-semibold text-blue-800">Browse Courses</div>
                  <div className="text-sm text-blue-600">Explore our course catalog</div>
                </a>
                <a href="/about" className="block p-3 bg-purple-50 hover:bg-purple-100 rounded-xl transition-colors">
                  <div className="font-semibold text-purple-800">About Us</div>
                  <div className="text-sm text-purple-600">Learn more about ExecuteAutomation</div>
                </a>
              </div>
            </div>

            {/* Contact Info */}
            <div className="bg-gradient-to-br from-green-500 to-emerald-600 rounded-2xl p-6 text-white">
              <h3 className="text-lg font-bold mb-4">Still Need Help?</h3>
              <p className="text-green-100 mb-4">
                Can't find what you're looking for? Our support team is here to help.
              </p>
              <a 
                href="mailto:karthik@techgeek.co.in"
                className="inline-block bg-white text-green-600 font-semibold px-4 py-2 rounded-lg hover:bg-green-50 transition-colors"
              >
                Email Support
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HelpPage;